import {
 type ReactNode,
 useEffect,
 useRef,
 useState
} from 'react'
import {
 useAuth
} from '../hooks/useAuth'
import {
 SignInHelpForm
} from './SignInHelpForm'
import {
 Navigate,
 useSearchParams
} from 'react-router-dom'
import {
 appendPostHogStitchParams,
 FeatureFlags,
 getFeatureFlagVariant,
 getFeatureFlagVariantSync,
 isFeatureFlagEnabled,
 peekFeatureFlagVariant,
 OnboardingEvents,
 setPersonProperties,
 trackEvent,
 trackReferralSignupContextShown
} from '../analytics/posthog'
import type {
 SignInProvider
} from '../analytics/signInReturn'
import {
 ArrowLeft,
 Building2,
 Check,
 ChevronDown,
 ChevronRight,
 Copy,
 ExternalLink,
 Gift,
 Loader2,
 MoreHorizontal,
 ShieldCheck,
 User
} from 'lucide-react'
import {
 authService,
 getApiErrorStatus,
 getUserFacingApiErrorMessage,
 hasApiErrorDetail
} from '../services/api'
import {
 captureReferralFromUrl,
 getValidStoredReferralCode
} from '../utils/referral'
import {
 buildSystemBrowserUrl,
 isInAppBrowser,
 isIOS,
 systemBrowserName
} from '../utils/inAppBrowser'
import {
 copyTextToClipboard
} from '../utils/clipboard'
import {
 GOOGLE_ICON,
 MICROSOFT_ICON
} from './ProviderIcons'
import {
 PresignupAssistantPicker
} from './PresignupAssistantPicker'
import {
 arrivedFromMarketing,
 loadPresignupAssistant,
 savePresignupAssistant
} from '../utils/presignupAssistant'
import type {
 PresignupAssistantChoice
} from '../utils/presignupAssistant'

// Shown when starting OAuth fails before we ever reach the provider — the
// backend 5xx'ing on /api/v1/auth/login, or the network being down. Deliberately
// says the sign-in did not start, so the user retries rather than assuming the
// click registered and waiting.
const SIGN_IN_START_FAILED =
  "We couldn't start sign-in. Please try again in a moment."

// Shown when enterprise SSO itself is unreachable. Distinct from the generic
// line because it names a working way forward, and the Google/Microsoft buttons
// it points at are on screen next to it.
const SSO_UNREACHABLE =
  "We couldn't reach your company's single sign-on. Try again, or sign in with Google or Microsoft instead."

/**
 * What to tell the user when a sign-in start throws.
 *
 * A named wrapper, so the call sites below read as one thing and so
 * `ssoFailureMessage` has something to delegate to. The 4xx/5xx rationale, and
 * the deliberate whitelist shape of the test, moved to the shared helper in
 * services/api.ts when the Settings page needed the same decision (GET-12886).
 */
function signInFailureMessage(error: unknown, fallback = SIGN_IN_START_FAILED): string {
  return getUserFacingApiErrorMessage(error, fallback)
}

/**
 * The SSO variant. `/auth/okta/login` fails two ways and they are not the same
 * sentence: a 404 means the domain has no connection (its `detail` is written
 * for a human and is better than anything we'd write), while a 502 means the
 * IdP was unreachable.
 *
 * A 502's detail is `str(OktaAuthError)` — raw upstream exception prose — so it
 * must never be surfaced; the hand-written sentence stands in for it. That is
 * also why this cannot simply be `signInFailureMessage(error, SSO_UNREACHABLE)`:
 * a 502 is a 5xx and would take the generic "couldn't start sign-in", losing
 * the one case where naming SSO is the useful thing to say.
 *
 * The status alone is NOT enough to conclude "the IdP is down". A crash-looping
 * backend makes the load balancer emit its own 502 with an HTML body and no
 * `detail`, which would blame the customer's identity provider for our outage —
 * the exact misattribution this function exists to prevent. So the sentence is
 * used only when the response also carries a FastAPI-shaped `detail`, i.e. our
 * handler really did run and really did fail to reach Okta.
 *
 * Everything else — our own 500, an infra 502, the network being down — takes
 * the generic line. Telling a customer their identity provider is unreachable
 * when the fault is ours sends their IT team after a system that is fine
 * (GET-12701).
 */
function ssoFailureMessage(error: unknown): string {
  if (getApiErrorStatus(error) === 502 && hasApiErrorDetail(error)) {
    return SSO_UNREACHABLE
  }
  return signInFailureMessage(error)
}

/** Forward a hint only when it's a real address — providers ignore junk. */
function validLoginHint(hint?: string): string | undefined {
  return hint && isValidEmail(hint) ? hint : undefined
}

/**
 * Record a failed sign-in start. Side effect only — the message shown to the
 * user is chosen separately, by `signInFailureMessage` / `ssoFailureMessage`.
 *
 * Analytics is the half the outage was missing. `SIGN_IN_FAILED` already fires
 * from CallbackPage for failures AFTER the provider redirect; nothing named the
 * pre-redirect class, so GET-12671 produced no event at all — only the derived
 * absence of `sign_in_completed`, which is why it ran 15 hours unnoticed.
 */
function reportSignInFailure(
  provider: SignInProvider,
  error: unknown,
): void {
  console.error(`${provider} login failed:`, error)
  trackEvent(OnboardingEvents.SIGN_IN_FAILED, {
    error: 'sign_in_start_failed',
    provider,
    status: getApiErrorStatus(error) ?? null,
  })
}

const OAUTH_BUTTON_CLASS =
  'w-full bg-white border border-catch-beige-dark text-catch-black px-6 py-4 rounded-xl font-medium hover:bg-catch-grey focus:outline-none focus:ring-2 focus:ring-catch-red/30 focus:ring-offset-2 transition-[background-color,box-shadow,transform] duration-200 shadow-soft hover:shadow-soft-lg flex items-center justify-center gap-3 active:scale-[0.96]'

function LoginBrand({ showHoneyBook }: { showHoneyBook: boolean }): JSX.Element {
  return (
    <div
      className="flex items-center justify-center gap-2.5 sm:gap-4"
      aria-label={showHoneyBook ? 'Catch and HoneyBook' : 'Catch'}
    >
      <div
        className={showHoneyBook
          // The Lottie canvas has transparent space after the visible Catch
          // wordmark. Offset it so the divider has equal visual breathing room.
          ? 'h-16 w-28 shrink-0 -mr-3 sm:h-[90px] sm:w-[168px] sm:-mr-7'
          : 'h-[90px] w-40'}
      >
        <dotlottie-player
          src="/lottie/logo-animation-red.lottie"
          autoplay
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      {showHoneyBook && (
        <>
          <div aria-hidden className="h-8 w-px shrink-0 bg-catch-black/15" />
          <div className="shrink-0 rounded-lg border border-[#E7C13E]/60 bg-[#FFD954] px-2 py-2 shadow-[0_3px_10px_rgba(33,33,33,0.08)] sm:px-3.5">
            <img
              src="/images/honeybook-wordmark.svg"
              alt="HoneyBook"
              className="h-auto w-[88px] sm:w-[112px]"
            />
          </div>
        </>
      )}
    </div>
  )
}

function NoticeCard({ icon, title, children }: {
  icon: JSX.Element
  title: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <div className="mx-auto max-w-sm p-4 bg-catch-beige border border-catch-beige-dark rounded-xl text-left">
      <div className="flex items-start gap-2">
        {icon}
        <div className="space-y-2">
          <p className="text-sm font-medium text-catch-black">{title}</p>
          {children}
        </div>
      </div>
    </div>
  )
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// Emit the acquisition-funnel CTA step for a sign-in initiated from the app
// login page. cta_clicked otherwise fires only on the marketing site, so the
// large share of signups that arrive directly at app.catchagent.ai/login (or
// the ?provider= deep link, which auto-fires OAuth) never produced step 2 and
// were dropped from the "User Acquisition Funnel - External users" insight even
// after subscribing. surface:'app' / cta_type:'app-login' keep it filterable
// out of landing-only CTA analyses.
function trackAppLoginCtaClicked(provider: 'google' | 'microsoft'): void {
  trackEvent(OnboardingEvents.CTA_CLICKED, {
    cta_type: 'app-login',
    provider,
    location: 'app-login',
    surface: 'app',
  })
}

type EmailProvider = 'google' | 'microsoft' | 'unknown'

interface DetectionResult {
  provider: EmailProvider
  domain: string
  // The domain belongs to an organization with enterprise SSO configured.
  // Independent of `provider`: an SSO user still connects Google or Microsoft
  // for calendar and mail, so both routes stay on offer.
  ssoAvailable: boolean
}

const PROVIDER_MESSAGES: Record<EmailProvider, string> = {
  google: 'Sign in with Google to connect your calendar and get started in seconds.',
  microsoft: 'Sign in with Microsoft to connect your calendar and get started in seconds.',
  unknown: 'Sign in with your work account to connect your calendar and get started.',
}

// `?reason=` is the backend's classification of how a sign-in died, and
// outranks the bare OAuth `?error=` code because the code alone misleads:
// AADSTS650051 arrives as `invalid_client` and is the user's tenant, not our
// registration (GET-13660). A reason with no entry here (`unknown`,
// `transient`) falls through to the code's own copy.
//
// Two backend enums feed this one slot, and the union below is their sum —
// keep it in step with both. `OAuthRefusalReason` (microsoft_token_service.py)
// is a provider that refused before issuing a code; `OAuthCallbackFailure`
// (oauth_callback_failure.py) is an exchange that raised after it did. Those
// used to have no classification at all: the page rendered the exception's own
// text, so a replayed authorization code told someone signing up
// `(invalid_grant) Bad Request` (GET-14095).
type RefusalReason =
  | 'user_cancelled'
  | 'consent_declined'
  | 'conditional_access'
  | 'guest_required'
  | 'tenant_unmanaged'
  | 'service_not_subscribed'
  | 'transient'
  | 'unknown'
  | 'code_already_used'
  | 'state_mismatch'
  | 'missing_code'
  | 'provider_rejected'
  | 'internal_error'
  | 'registered_with_google'
  | 'registered_with_microsoft'
  | 'account_deactivated'

const REASON_MESSAGES: Partial<Record<RefusalReason, string>> = {
  user_cancelled: 'Sign-in was cancelled before it finished. Whenever you\'re ready, try again.',
  consent_declined: 'Catch needs permission to access your mail and calendar to do this work. Your IT admin may need to approve Catch for your organization.',
  conditional_access: 'Your organization\'s security policy is stopping this sign-in. Ask your IT admin to allow Catch, then try again.',
  guest_required: 'This account can only sign in as a guest of another organization. Try a different account.',
  tenant_unmanaged: 'Your organization\'s Microsoft account isn\'t set up for apps yet. Ask your IT admin to verify your domain in Microsoft Entra, then try again.',
  service_not_subscribed: 'Your Microsoft plan doesn\'t include the mail and calendar service Catch works with. Ask your IT admin, or try a different account.',
  // Reached when the browser has no session to resume: either the sign-in
  // finished in another tab/navigation, or the code expired before it was
  // exchanged. Deliberately does not say "your account is ready" — `invalid_
  // grant` covers the expired case too, where no account was created, and one
  // sentence has to be true of both. The action is the same either way, which
  // is the sentence the user actually needs: the replayed-code case that
  // motivated this had a working account and read `(invalid_grant) Bad
  // Request` instead (GET-14095).
  code_already_used: 'That sign-in was already used, or it expired. Sign in again to pick up where you left off.',
  state_mismatch: 'Your browser didn\'t hold on to this sign-in. Try again — if it keeps happening, check that cookies are enabled for this site.',
  provider_rejected: 'Your sign-in provider didn\'t complete this sign-in. Try again, or try a different account.',
  internal_error: 'Something went wrong on our side. Try again — if it keeps happening, let us know.',
  // The address belongs to an account on the other provider. Usually a company
  // that just moved its mail, and support can move the account in place
  // (GET-14054), so the copy says so instead of "something went wrong".
  registered_with_google: 'This address is set up with Google, so sign in with Google instead. If your company has moved its email to Microsoft, write to support@catchagent.ai and we\'ll move your account across.',
  registered_with_microsoft: 'This address is set up with Microsoft, so sign in with Microsoft instead. If your company has moved its email to Google, write to support@catchagent.ai and we\'ll move your account across.',
  // Same sentence as DEACTIVATED_ACCOUNT_SIGN_IN_MESSAGE in the backend.
  account_deactivated: 'This account has been deactivated. Please contact support to reopen it.',
  // `missing_code` is deliberately absent: nothing a person's browser does
  // produces it (it is scanners and link prefetchers hitting the bare callback
  // URL), so it falls through to the unclassified copy rather than earning a
  // sentence written for a reader who does not exist.
}

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Access was denied. Please try again or contact support.',
  invalid_request: 'The login request was invalid. Please try again.',
  server_error: 'Your sign-in provider had a temporary issue. Try again now.',
  temporarily_unavailable: 'Your sign-in provider is temporarily unavailable. Try again in a moment.',
}

// A provider refusal always arrives with `?reason=`, and its `?error=` is a
// bare OAuth code — so a code neither table knows gets this rather than the
// token itself. Without `?reason=`, `?error=` is one of the other callback
// exits' full sentences and renders as written.
const UNCLASSIFIED_REFUSAL_MESSAGE = 'We couldn\'t complete the sign-in. Try again, or try a different account.'

// Failures the sign-in help form (GET-20068) is not offered for: the banner
// above already names the fix (sign in with Google, or simply sign in again),
// or the return was a link scanner with no person behind it. `user_cancelled`
// stays offered: Microsoft's "Need admin approval" exit reads the same way.
const SELF_SERVE_REASONS: ReadonlySet<string> = new Set<RefusalReason>([
  'registered_with_google',
  'code_already_used',
  'missing_code',
])

const reasonMessage = (reason: string | null): string | undefined =>
  reason === null ? undefined : REASON_MESSAGES[reason as RefusalReason]

/**
 * Count a sign-in that the backend bounced back to this page.
 *
 * The third emitter of `SIGN_IN_FAILED`, and the one that was missing:
 * CallbackPage fires for a failure carried to `/callback`, `reportSignInFailure`
 * for one that never reached the provider, and this branch — a provider or
 * exchange failure redirected to `/login?error=` — rendered a banner and fired
 * nothing. So the class had no event at all: ~130 distinct people land on such
 * a URL in 30 days and none of them were countable, which is the same shape as
 * the absence GET-12671 closed one branch over.
 *
 * `error` is the class, matching the other two emitters, so a breakdown on it
 * gives three buckets; `reason` carries the why.
 *
 * `?error=` itself is deliberately NOT sent. It is a closed token only for the
 * exits that classify: the others put a full sentence there, and the
 * wrong-account one interpolates the address the person just signed in with —
 * so forwarding it would publish an email address to analytics. Same reasoning
 * that keeps `error` out of `ANALYTICS_PARAMS` in `analytics/urlSanitizer.ts`;
 * an event property is no safer a home for it than a URL.
 */
function reportRedirectedSignInFailure(reason: string | null): void {
  trackEvent(OnboardingEvents.SIGN_IN_FAILED, {
    error: 'callback_redirect_failed',
    reason: reason ?? 'unclassified',
  })
}

function isProviderDeepLink(searchParams: URLSearchParams): boolean {
  const provider = searchParams.get('provider')
  return provider === 'gmail' || provider === 'outlook'
}

export function LoginPage(): JSX.Element {
  const { isAuthenticated, login, loginWithMicrosoft, loginWithSSO } = useAuth()
  const [searchParams] = useSearchParams()
  const showHoneyBookBrand = searchParams.get('source')?.trim().toLowerCase() === 'honeybook'
  const errorParam = searchParams.get('error')
  const reasonParam = searchParams.get('reason')
  const errorFromRedirect = errorParam
    ? (reasonMessage(reasonParam)
      ?? ERROR_MESSAGES[errorParam]
      ?? (reasonParam === null ? errorParam : UNCLASSIFIED_REFUSAL_MESSAGE))
    : null
  // A sign-in start that never reached the provider. `errorFromRedirect` only
  // ever carries a failure the provider bounced BACK to us, so before this the
  // whole "we couldn't even ask" class had nowhere to render and the OAuth
  // handlers logged it to the console — the button simply did nothing
  // (GET-12701: a 15h /api/v1/auth/login 500 read to users as a dead button,
  // and one of them cancelled).
  //
  // It feeds the `error` banner rather than `emailError`, because `emailError`
  // renders only inside `emailForm` — which the post-detection branch, the one
  // holding the SSO button, never renders. Every branch that draws a sign-in
  // control draws this banner: the `?provider=` card, the `?provider=`
  // auto-fire fallback, and the main card. (The fourth branch, the
  // `isAuthenticated` redirect, draws no UI at all.)
  const [signInError, setSignInError] = useState<string | null>(null)
  const error = signInError ?? errorFromRedirect
  // A Microsoft sign-in that came back failed gets a way to ask support for
  // help (GET-20068), for the whole visit, so a retry that fails differently
  // doesn't remount the form and lose what was sent. Only a classified failure:
  // the exits that send no `?reason=` (a personal account, an SSO-only org, the
  // wrong account) write their own banner sentence naming the fix.
  const showSignInHelp = errorParam !== null
    && searchParams.get('signin_provider') === 'microsoft'
    && reasonParam !== null
    && !SELF_SERVE_REASONS.has(reasonParam)
  const [showPermissions, setShowPermissions] = useState(false)

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isDetecting, setIsDetecting] = useState(false)
  const [detectionResult, setDetectionResult] = useState<DetectionResult | null>(null)
  const [transitionEnabled, setTransitionEnabled] = useState<boolean | null>(null)
  // Treatment-group only: gates the notice, button relabel, and escape redirect.
  // Detection telemetry (`in_app_browser_detected`) still fires for everyone so
  // we keep a denominator for the A/B test.
  const [escapeEnabled, setEscapeEnabled] = useState(false)
  // iOS in-app browsers (Instagram/Facebook WKWebView) silently ignore every
  // known programmatic escape, so on iOS the treatment renders a manual
  // "Open in Browser" instruction instead of attempting a redirect.
  const [iosInstructionMode, setIosInstructionMode] = useState(false)
  // Bumped each time the user taps OAuth while iosInstructionMode is active
  // so a useEffect can scroll the instruction card into view and replay the
  // pulse animation — gives visible feedback for an otherwise no-op tap.
  const [instructionPulseTick, setInstructionPulseTick] = useState(0)
  const instructionCardRef = useRef<HTMLDivElement | null>(null)
  type CopyState = 'idle' | 'copied' | 'failed'
  const [copyState, setCopyState] = useState<CopyState>('idle')
  const [referralCode, setReferralCode] = useState<string | null>(null)
  // GET-18726. Decided on the first render from PostHog's persisted flags when
  // it has them; otherwise 'pending' until the flag resolves, so neither arm
  // flashes the other's screen. A visitor who already picked (and came back
  // without finishing sign-in) is not asked again, and not counted as exposed.
  // Only a visit from the landing site or an ad takes part; anyone else on
  // /login is signing back in, and goes straight to the buttons (GET-20223).
  const fromMarketing = arrivedFromMarketing(searchParams, document.referrer)
  const [assistantPicker, setAssistantPicker] = useState<'pending' | 'show' | 'done'>(() => {
    if (!fromMarketing || loadPresignupAssistant() !== null) return 'done'
    const cached = peekFeatureFlagVariant(FeatureFlags.PRESIGNUP_ASSISTANT_PICKER)
    if (cached === undefined) return 'pending'
    return cached === 'test' ? 'show' : 'done'
  })
  const [pickedAssistantName, setPickedAssistantName] = useState<string | null>(null)
  // On a `?provider=` deep link the picker holds the auto-fired OAuth back;
  // this resumes it once the visitor has picked (or skipped).
  const resumeProviderFlowRef = useRef<(() => void) | null>(null)

  const escapeUrlForProvider = (providerKey: 'gmail' | 'outlook'): string => {
    const url = new URL(window.location.href)
    url.searchParams.set('provider', providerKey)
    // Embed the current PostHog distinct_id/session_id so the receiving
    // browser bootstraps as the same Person — without this, Safari/Chrome
    // gets a fresh anon ID and we can't link conversion back to the
    // original webview visit. See appendPostHogStitchParams docs.
    appendPostHogStitchParams(url)
    return url.toString()
  }

  const tryEscapeForProvider = (providerKey: 'gmail' | 'outlook'): boolean => {
    const target = escapeUrlForProvider(providerKey)
    const escapeHref = buildSystemBrowserUrl(target)
    if (!escapeHref) return false
    trackEvent(OnboardingEvents.IN_APP_BROWSER_ESCAPE_ATTEMPTED, {
      provider: providerKey,
      platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios' : 'android',
    })
    window.location.href = escapeHref
    return true
  }

  // iOS in-app webviews have no working programmatic escape, so an OAuth tap
  // would either be silently suppressed or trigger Google's
  // disallowed_useragent error. Give the user visible feedback pointing them
  // at the instruction card instead.
  const handleIosInstructionOauthTap = (provider: 'google' | 'microsoft'): void => {
    trackEvent(OnboardingEvents.SIGN_IN_CLICKED, {
      provider,
      blocked_reason: 'ios_no_escape',
    })
    // Clear here, not in the handlers: their `setSignInError(null)` sits AFTER
    // the early return into this function, and unlike the escape redirect this
    // branch stays on the page. Buttons are tappable from first paint while the
    // flag effect resolves (up to 1.5s), so a tap that failed before the flag
    // landed would leave "try again in a moment" on screen above a card whose
    // taps now do nothing but pulse.
    setSignInError(null)
    setInstructionPulseTick((tick) => tick + 1)
  }

  /**
   * On the `?provider=` deep link the page auto-fires OAuth and renders a
   * deliberate blank while the browser leaves. When the start FAILS the browser
   * never leaves, so that arm has to become something the user can act on —
   * showing the error alone still dead-ends them, with a reload as the only
   * exit. Switching to the transition layout reuses the branch below, which
   * already renders this banner plus a working provider button and an
   * other-provider link.
   *
   * A no-op anywhere else: `transitionEnabled` is read only inside the
   * `?provider=` branch. The mount effect has `[]` deps, so this cannot
   * re-trigger the auto-fire. It does put a failed control-arm session on the
   * transition layout, which slightly muddies that flag's A/B — accepted,
   * because the alternative is a dead end.
   */
  const revealRetryAfterFailedStart = (): void => {
    setTransitionEnabled(true)
  }

  const handleGoogleLogin = async (loginHint?: string): Promise<void> => {
    if (iosInstructionMode) {
      handleIosInstructionOauthTap('google')
      return
    }
    trackEvent(OnboardingEvents.SIGN_IN_CLICKED, { provider: 'google' })
    trackAppLoginCtaClicked('google')
    if (escapeEnabled && tryEscapeForProvider('gmail')) return
    setSignInError(null)
    try {
      // Pass the typed work email as a hint so Google pre-selects it in the
      // account chooser instead of silently reusing a personal-Gmail session
      // (GET-8405). Only forward a valid address; Google ignores junk hints.
      await login(validLoginHint(loginHint))
    } catch (error) {
      reportSignInFailure('google', error)
      setSignInError(signInFailureMessage(error))
      revealRetryAfterFailedStart()
    }
  }

  const handleMicrosoftLogin = async (loginHint?: string): Promise<void> => {
    if (iosInstructionMode) {
      handleIosInstructionOauthTap('microsoft')
      return
    }
    trackEvent(OnboardingEvents.SIGN_IN_CLICKED, { provider: 'microsoft' })
    trackAppLoginCtaClicked('microsoft')
    if (escapeEnabled && tryEscapeForProvider('outlook')) return
    setSignInError(null)
    try {
      // Same contract as the Google path: forward the typed work email so
      // Microsoft's chooser preselects it. It also tells the backend this is a
      // deliberate switch rather than a reconnect of the signed-in account,
      // which matters when the switch-to-work hand-off arrives here with a
      // personal Outlook session still alive.
      await loginWithMicrosoft(validLoginHint(loginHint))
    } catch (error) {
      reportSignInFailure('microsoft', error)
      setSignInError(signInFailureMessage(error))
      revealRetryAfterFailedStart()
    }
  }

  const handleSSOLogin = async (ssoEmail: string): Promise<void> => {
    trackEvent(OnboardingEvents.SIGN_IN_CLICKED, { provider: 'sso' })
    setSignInError(null)
    try {
      await loginWithSSO(ssoEmail)
    } catch (error) {
      // `signInError`, not `emailError` — see the state declaration: this
      // branch doesn't render `emailForm`, so the old message was set but
      // never shown to anyone.
      reportSignInFailure('sso', error)
      setSignInError(ssoFailureMessage(error))
      revealRetryAfterFailedStart()
    }
  }

  const handleCopyLoginLink = async (): Promise<void> => {
    // Mirror the escape URL so the pasted link also stitches the new
    // browser back to the original PostHog Person.
    const url = new URL(window.location.href)
    appendPostHogStitchParams(url)
    try {
      await copyTextToClipboard(url.toString())
    } catch {
      setCopyState('failed')
      return
    }
    setCopyState('copied')
    trackEvent(OnboardingEvents.IN_APP_BROWSER_LINK_COPIED)
    setTimeout(() => setCopyState('idle'), 2000)
  }

  useEffect(() => {
    void import('@dotlottie/player-component')
  }, [])

  useEffect(() => {
    if (instructionPulseTick === 0) return
    const el = instructionCardRef.current
    if (!el) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
    if (reduceMotion) return
    // Remove → force reflow → re-add so the keyframe restarts cleanly on
    // rapid taps, not just the first one.
    el.classList.remove('animate-pulse-once')
    void el.offsetWidth
    el.classList.add('animate-pulse-once')
  }, [instructionPulseTick])

  useEffect(() => {
    // Re-capture on mount: the module-level capture in App.tsx only runs on a
    // full page load, not on client-side navigation to /login?ref=... .
    captureReferralFromUrl()
    const storedReferral = getValidStoredReferralCode()
    if (storedReferral) {
      setReferralCode(storedReferral)
      trackReferralSignupContextShown(storedReferral)
    }

    const paramsString = searchParams.toString()
    if (paramsString) {
      sessionStorage.setItem('login_params', paramsString)
    }
    trackEvent(OnboardingEvents.SIGN_IN_PAGE_VIEWED)

    // Here rather than in its own effect keyed on the params: this one already
    // runs once per mount, and a `?error=` only ever arrives with the
    // navigation that mounts the page. An effect keyed on `errorFromRedirect`
    // would re-fire on any later render that recomputed the same banner.
    if (errorParam) {
      reportRedirectedSignInFailure(reasonParam)
    }
    if (showSignInHelp) {
      trackEvent(OnboardingEvents.SIGN_IN_HELP_SHOWN, { provider: 'microsoft', reason: reasonParam })
    }

    const detectedInApp = isInAppBrowser()
    if (detectedInApp) {
      trackEvent(OnboardingEvents.IN_APP_BROWSER_DETECTED, {
        user_agent: navigator.userAgent,
      })
    }

    const providerParam = searchParams.get('provider')
    const hasProviderParam = isProviderDeepLink(searchParams)

    // Resolve the login-transition-page flag and the picker arm in parallel,
    // with a 1.5s timeout so a slow PostHog response can't block the render.
    // The in-app-browser escape always applies (its A/B concluded, GET-20081).
    const escapeOn = detectedInApp
    const transitionFlagPromise = hasProviderParam
      ? isFeatureFlagEnabled(FeatureFlags.LOGIN_TRANSITION_PAGE)
      : Promise.resolve(false)
    const pickerEligible = fromMarketing && loadPresignupAssistant() === null
    const pickerFlagPromise = assistantPicker === 'pending'
      ? getFeatureFlagVariant(FeatureFlags.PRESIGNUP_ASSISTANT_PICKER)
      : Promise.resolve(undefined)
    const timeoutSentinel = new Promise<'timeout'>((resolve) =>
      setTimeout(() => resolve('timeout'), 1500),
    )

    void Promise.race([
      Promise.all([transitionFlagPromise, pickerFlagPromise]),
      timeoutSentinel,
    ]).then((result) => {
      const [transitionOn, pickerVariant] = result === 'timeout'
        ? [false, undefined]
        : result
      // Not inside an in-app browser we are about to leave: the pick would stay
      // in the webview's storage. The escaped browser asks instead.
      const escaping = escapeOn
      // The exposure is recorded here, only when the arm decides what is
      // rendered: not for a visitor who already picked, not when the flag
      // lands after the timeout, and not for one the escape keeps on control.
      const armDecided = assistantPicker !== 'pending' || result !== 'timeout'
      if (pickerEligible && armDecided && !escaping) {
        getFeatureFlagVariantSync(FeatureFlags.PRESIGNUP_ASSISTANT_PICKER)
      }
      const showPicker = !escaping && (
        assistantPicker === 'pending'
          ? pickerVariant === 'test'
          : assistantPicker === 'show'
      )
      setAssistantPicker(showPicker ? 'show' : 'done')
      if (escapeOn) {
        setEscapeEnabled(true)
        // Embed stitch params in the visible URL so when a user opens the
        // page in their system browser via the in-app menu (vs the Copy
        // link button), the URL Safari/Chrome copies already carries the
        // PostHog distinct_id/session_id and the receiving session
        // bootstraps onto the same Person. Without this, manual "Open in
        // Safari" produces a fresh anon ID and we lose stitched
        // conversion data — which on iOS is the dominant path because
        // x-safari-https:// silently fails.
        const currentUrl = new URL(window.location.href)
        if (!currentUrl.searchParams.has('ph_distinct_id')) {
          appendPostHogStitchParams(currentUrl)
          window.history.replaceState(null, '', currentUrl.toString())
        }
        if (isIOS()) {
          setIosInstructionMode(true)
          trackEvent(OnboardingEvents.IN_APP_BROWSER_INSTRUCTION_SHOWN, {
            user_agent: navigator.userAgent,
          })
        }
      }

      if (!hasProviderParam) return

      const startProviderFlow = (useTransitionPage: boolean): void => {
        if (escapeOn) {
          // Don't auto-fire OAuth inside the webview — render the transition
          // page so the user can tap the button, which triggers the
          // system-browser escape via handleGoogleLogin.
          setTransitionEnabled(true)
          return
        }
        // No in-app browser detected: show the transition page if its flag is
        // on, else auto-fire OAuth.
        if (useTransitionPage) {
          setTransitionEnabled(true)
          return
        }
        setTransitionEnabled(false)
        void (providerParam === 'gmail' ? handleGoogleLogin() : handleMicrosoftLogin())
      }

      if (showPicker) {
        // Start OAuth straight after the pick: the visitor has just clicked
        // through one screen, so the transition page would be a second.
        resumeProviderFlowRef.current = () => startProviderFlow(false)
        return
      }
      startProviderFlow(transitionOn)
    })
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  const provider = searchParams.get('provider')

  const finishAssistantPicker = (): void => {
    setAssistantPicker('done')
    resumeProviderFlowRef.current?.()
    resumeProviderFlowRef.current = null
  }

  const handleAssistantChosen = (choice: PresignupAssistantChoice, assistantName: string): void => {
    savePresignupAssistant(choice)
    trackEvent(OnboardingEvents.PRESIGNUP_PICKER_PICKED, { kind: choice.kind })
    setPickedAssistantName(assistantName)
    finishAssistantPicker()
  }

  const handleSkipAssistantPicker = (): void => {
    trackEvent(OnboardingEvents.PRESIGNUP_PICKER_SKIPPED_TO_LOGIN)
    finishAssistantPicker()
  }

  const browserName = systemBrowserName()
  // The "Open in <Browser>" button label only makes sense when there's a
  // working escape redirect. iOS instruction-mode suppresses the redirect,
  // so revert to the plain OAuth label there.
  const useEscapeLabel = escapeEnabled && !iosInstructionMode
  const copyLinkButton = (
    <>
      <button
        onClick={() => void handleCopyLoginLink()}
        className="inline-flex items-center gap-1.5 text-xs text-catch-black/70 hover:text-catch-black underline underline-offset-2 transition-colors"
      >
        {copyState === 'copied' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copyState === 'copied' ? 'Link copied' : 'Copy link'}
      </button>
      {copyState === 'failed' && (
        <p className="text-xs text-catch-red leading-relaxed break-all select-all">
          Couldn&rsquo;t copy automatically — long-press to copy:{' '}
          <span className="underline">{window.location.href}</span>
        </p>
      )}
    </>
  )

  const referralNotice = referralCode ? (
    <NoticeCard
      icon={<Gift className="w-4 h-4 text-catch-red flex-shrink-0 mt-0.5" />}
      title="A friend has you covered."
    >
      <p className="text-xs text-catch-black/60 leading-relaxed">
        Catch is your admin savior — scheduling, email, and the rest of the
        boring work, taken off your plate.
      </p>
      <p className="text-xs text-catch-black/60 leading-relaxed">
        Your invite includes{' '}
        <strong className="font-semibold text-catch-black/80">50% off your first month</strong>
        {' '}— applied automatically at checkout, after your free 10-day trial.
      </p>
    </NoticeCard>
  ) : null

  const inAppBrowserNotice = escapeEnabled ? (
    iosInstructionMode ? (
      <div
        ref={instructionCardRef}
        className="mx-auto max-w-sm p-5 bg-catch-beige border-2 border-catch-red rounded-xl text-left shadow-soft"
      >
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <ExternalLink className="w-5 h-5 text-catch-red flex-shrink-0 mt-0.5" />
            <p className="text-base font-semibold text-catch-black">
              Open this page in {browserName} to sign in
            </p>
          </div>
          <p className="text-sm text-catch-black/70 leading-relaxed">
            Google won&rsquo;t let you sign in here — you&rsquo;ll need a real browser.
          </p>
          <ol className="text-sm text-catch-black/80 space-y-2 list-none pl-0">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-catch-black text-catch-white text-xs font-semibold flex items-center justify-center mt-0.5">1</span>
              <span>
                Tap the{' '}
                <span className="inline-flex items-center justify-center w-5 h-5 align-middle rounded bg-catch-black/5 border border-catch-black/15">
                  <MoreHorizontal className="w-3.5 h-3.5 text-catch-black" />
                </span>
                {' '}menu at the corner of the screen
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-catch-black text-catch-white text-xs font-semibold flex items-center justify-center mt-0.5">2</span>
              <span>Choose <strong>&ldquo;Open in {browserName}&rdquo;</strong></span>
            </li>
          </ol>
          <div className="pt-1 space-y-1">
            <p className="text-xs text-catch-black/50">Or paste the link into {browserName} yourself:</p>
            {copyLinkButton}
          </div>
        </div>
      </div>
    ) : (
      <NoticeCard
        icon={<ExternalLink className="w-4 h-4 text-catch-black/60 flex-shrink-0 mt-0.5" />}
        title={<>Open in {browserName} to sign in</>}
      >
        <p className="text-xs text-catch-black/60 leading-relaxed">
          Google blocks sign-in inside in-app browsers. Tap the button below to switch to {browserName} — or open the menu and choose &ldquo;Open in Browser&rdquo;.
        </p>
        {copyLinkButton}
      </NoticeCard>
    )
  ) : null

  if (isAuthenticated) {
    return <Navigate to="/integrations" replace />
  }

  if (assistantPicker === 'show') {
    return (
      <PresignupAssistantPicker
        onChoose={handleAssistantChosen}
        onSkipToLogin={handleSkipAssistantPicker}
      />
    )
  }

  if (provider === 'gmail' || provider === 'outlook') {
    if (transitionEnabled !== true) {
      // OAuth is auto-firing (see the mount effect): this screen is a
      // deliberate blank while the browser leaves for the provider. Before
      // GET-12701 it was a bare div, so a failed start — where the browser
      // never leaves — wrote `setSignInError` to state with nowhere to render
      // and left the user on an empty page indefinitely, the dead-button
      // symptom in its worst form on ~4% of login traffic (108 of 2,523
      // pageviews over the 14 days to 2026-08-25).
      //
      // A failed start now calls `revealRetryAfterFailedStart`, so it lands on
      // the branch below with a working button rather than here. What is left
      // for this slot is the redirect case: arriving with BOTH `?provider=` and
      // `?error=`, where the message must not be swallowed by the blank.
      return (
        <div className="min-h-dvh bg-catch-beige flex items-center justify-center px-4">
          {error && (
            <div
              role="alert"
              className="max-w-sm w-full p-4 bg-catch-red/10 border border-catch-red/20 rounded-lg"
            >
              <p className="text-sm text-catch-red">{error}</p>
            </div>
          )}
        </div>
      )
    }
    const isGoogle = provider === 'gmail'
    const handleProviderClick = (): void => {
      void (isGoogle ? handleGoogleLogin() : handleMicrosoftLogin())
    }
    const otherProviderLabel = isGoogle ? 'Microsoft' : 'Google'
    const handleOtherProvider = (): void => {
      void (isGoogle ? handleMicrosoftLogin() : handleGoogleLogin())
    }

    return (
      <div className="min-h-dvh flex items-center justify-center relative overflow-hidden bg-catch-beige px-4">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(120% 80% at 0% 0%, #F4D6D6 0%, rgba(244,214,214,0) 55%),'
              + 'radial-gradient(110% 70% at 100% 0%, #F3DFC4 0%, rgba(243,223,196,0) 55%),'
              + 'radial-gradient(130% 90% at 100% 100%, #C5E0D2 0%, rgba(197,224,210,0) 55%),'
              + 'radial-gradient(130% 90% at 0% 100%, #CFD9E5 0%, rgba(207,217,229,0) 55%)',
          }}
        />

        <div className="relative z-10 max-w-xl w-full text-center animate-fade-in">
          <LoginBrand showHoneyBook={showHoneyBookBrand} />

          <h1 className="font-display text-3xl md:text-[2.5rem] leading-[1.1] text-catch-black mt-6">
            Your admin savior is{' '}
            <span className="font-serif italic">one sign-in away.</span>
          </h1>

          <p className="mt-4 text-base md:text-lg text-catch-black/55">
            Free to start. 10-day trial. No commitment.
          </p>

          {referralNotice && <div className="mt-6">{referralNotice}</div>}

          {error && (
            <div
              role="alert"
              className="mt-6 mx-auto max-w-sm p-3 bg-catch-red/10 border border-catch-red/20 rounded-lg"
            >
              <p className="text-sm text-catch-red">{error}</p>
            </div>
          )}

          {inAppBrowserNotice && <div className="mt-8">{inAppBrowserNotice}</div>}

          <button
            onClick={handleProviderClick}
            className="mt-10 inline-flex items-center gap-3 bg-catch-white border border-catch-beige-dark text-catch-black px-7 py-4 rounded-full font-medium shadow-soft hover:shadow-soft-lg transition-[background-color,box-shadow,transform] duration-200 focus:outline-none focus:ring-2 focus:ring-catch-red/30 focus:ring-offset-2 active:scale-[0.96]"
          >
            {isGoogle ? GOOGLE_ICON : MICROSOFT_ICON}
            {useEscapeLabel ? `Open in ${browserName}` : `Continue with ${isGoogle ? 'Google' : 'Microsoft'}`}
          </button>

          <p className="mt-6 text-sm text-catch-black/45">
            Private by default. What's yours stays yours.
          </p>

          <button
            onClick={handleOtherProvider}
            className="mt-4 text-sm text-catch-black/45 hover:text-catch-black underline underline-offset-2 transition-colors"
          >
            Sign in with {otherProviderLabel} instead
          </button>

          <p className="mt-8 text-xs text-catch-black/40">
            By continuing, you agree to our{' '}
            <a href="/terms-new" className="underline hover:text-catch-black/60 transition-colors">Terms</a>
            {' '}and{' '}
            <a href="/privacy" className="underline hover:text-catch-black/60 transition-colors">Privacy Policy</a>.
          </p>
        </div>
      </div>
    )
  }

  if (assistantPicker === 'pending') {
    // At most the 1.5s flag timeout, and the same blank in both arms.
    return <div className="min-h-dvh bg-catch-beige" />
  }

  const handleEmailSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setEmailError(null)
    // Also clear the sign-in banner: a failed OAuth attempt is not a fact about
    // the email the user is now submitting, and leaving it up puts "we couldn't
    // start sign-in" at the top of the post-detection screen — exactly where we
    // are offering them a route that hasn't failed.
    setSignInError(null)

    const trimmed = email.trim()
    if (!trimmed) {
      setEmailError('Please enter your work email')
      return
    }
    if (!isValidEmail(trimmed)) {
      setEmailError('Please enter a valid email address')
      return
    }
    const emailDomain = trimmed.split('@')[1]
    setPersonProperties({ email: trimmed })
    trackEvent(OnboardingEvents.EMAIL_PROVIDER_DETECT_SUBMITTED, { email_domain: emailDomain })
    setIsDetecting(true)

    try {
      const result = await authService.detectEmailProvider(trimmed)
      const detection: DetectionResult = {
        provider: result.provider as EmailProvider,
        domain: result.domain,
        ssoAvailable: result.sso_available ?? false,
      }
      setDetectionResult(detection)
      trackEvent(OnboardingEvents.EMAIL_PROVIDER_DETECTED, {
        provider: detection.provider,
        domain: detection.domain,
        sso_available: detection.ssoAvailable,
      })
    } catch {
      setDetectionResult({ provider: 'unknown', domain: emailDomain ?? '', ssoAvailable: false })
    } finally {
      setIsDetecting(false)
    }
  }

  const handleBack = (): void => {
    setDetectionResult(null)
    setEmailError(null)
    setSignInError(null)
  }

  const emailForm = (
    <form onSubmit={(e) => void handleEmailSubmit(e)} className="space-y-3">
      <div>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailError(null) }}
          placeholder="Enter your work email"
          className="w-full px-4 py-3 bg-catch-beige border border-catch-beige-dark rounded-xl focus:outline-none focus:ring-2 focus:ring-catch-red/30 focus:border-catch-red/50 text-catch-black placeholder-catch-black/40 transition-[box-shadow,border-color]"
          disabled={isDetecting}
        />
        {emailError && (
          <p className="mt-1 text-sm text-catch-red">{emailError}</p>
        )}
      </div>
      <button
        type="submit"
        disabled={isDetecting}
        className="w-full bg-catch-black text-catch-white px-6 py-3 rounded-xl font-medium hover:bg-catch-black/90 focus:outline-none focus:ring-2 focus:ring-catch-red/30 focus:ring-offset-2 transition-[background-color,box-shadow,transform] duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.96]"
      >
        {isDetecting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking...
          </>
        ) : (
          'Continue with email'
        )}
      </button>
    </form>
  )

  return (
    <div className="min-h-dvh bg-catch-beige flex items-center justify-center relative overflow-hidden">
      <div className="max-w-md w-full mx-4 animate-fade-in relative z-10">
        <div
          className={`bg-catch-white rounded-2xl shadow-soft-lg ${showHoneyBookBrand ? 'p-6 sm:p-10' : 'p-10'}`}
        >
          <div className="flex flex-col items-center mb-8">
            <LoginBrand showHoneyBook={showHoneyBookBrand} />
            <p className="text-sm text-catch-black/60 -mt-1">
              {pickedAssistantName ? `Sign in to meet ${pickedAssistantName}` : 'Your admin savior'}
            </p>
          </div>

          {referralNotice && <div className="mb-6">{referralNotice}</div>}

          {error && (
            <div
              role="alert"
              className="mb-6 p-4 bg-catch-red/10 border border-catch-red/20 rounded-lg"
            >
              <p className="text-sm text-catch-red">{error}</p>
            </div>
          )}

          {showSignInHelp && (
            <SignInHelpForm provider="microsoft" reason={reasonParam} initialEmail={email} />
          )}

          {inAppBrowserNotice && <div className="mb-6">{inAppBrowserNotice}</div>}

          {detectionResult ? (
            <div className="space-y-4">
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-1 text-sm text-catch-black/50 hover:text-catch-black transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <p className="text-sm text-catch-black/60 text-center leading-relaxed">
                {detectionResult.ssoAvailable
                  ? "Your company uses single sign-on. Use the same login as everything else at work — you'll connect your calendar next."
                  : PROVIDER_MESSAGES[detectionResult.provider]}
              </p>

              <div className="space-y-3">
                {detectionResult.ssoAvailable && (
                  <>
                    <button
                      onClick={() => void handleSSOLogin(email)}
                      className={OAUTH_BUTTON_CLASS}
                    >
                      <Building2 className="w-5 h-5" />
                      Continue with single sign-on
                    </button>
                    <div className="flex items-center gap-3 py-1" aria-hidden="true">
                      <span className="h-px flex-1 bg-catch-beige-dark" />
                      <span className="text-xs text-catch-black/40">or</span>
                      <span className="h-px flex-1 bg-catch-beige-dark" />
                    </div>
                  </>
                )}

                {(detectionResult.provider === 'google' || detectionResult.provider === 'unknown') && (
                  <button
                    onClick={() => void handleGoogleLogin(email)}
                    className={OAUTH_BUTTON_CLASS}
                  >
                    {GOOGLE_ICON}
                    {useEscapeLabel ? `Open Google sign-in in ${browserName}` : 'Continue with Google'}
                  </button>
                )}

                {(detectionResult.provider === 'microsoft' || detectionResult.provider === 'unknown') && (
                  <button
                    onClick={() => void handleMicrosoftLogin(email)}
                    className={OAUTH_BUTTON_CLASS}
                  >
                    {MICROSOFT_ICON}
                    {useEscapeLabel ? `Open Microsoft sign-in in ${browserName}` : 'Continue with Microsoft'}
                  </button>
                )}
              </div>

              {detectionResult.provider === 'google' && (
                <div className="text-center">
                  <button
                    onClick={() => void handleMicrosoftLogin(email)}
                    className="text-sm text-catch-black/50 hover:text-catch-black underline transition-colors"
                  >
                    Sign in with Microsoft instead
                  </button>
                </div>
              )}
              {detectionResult.provider === 'microsoft' && (
                <div className="text-center">
                  <button
                    onClick={() => void handleGoogleLogin(email)}
                    className="text-sm text-catch-black/50 hover:text-catch-black underline transition-colors"
                  >
                    Sign in with Google instead
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <button
                  onClick={() => void handleGoogleLogin()}
                  className={OAUTH_BUTTON_CLASS}
                >
                  {GOOGLE_ICON}
                  {useEscapeLabel ? `Open Google sign-in in ${browserName}` : 'Continue with Google'}
                </button>

                <button
                  onClick={() => void handleMicrosoftLogin()}
                  className={OAUTH_BUTTON_CLASS}
                >
                  {MICROSOFT_ICON}
                  {useEscapeLabel ? `Open Microsoft sign-in in ${browserName}` : 'Continue with Microsoft'}
                </button>
              </div>

              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 border-t border-catch-beige-dark" />
                <span className="text-sm text-catch-black/40">OR</span>
                <div className="flex-1 border-t border-catch-beige-dark" />
              </div>

              {emailForm}
            </>
          )}

          <div className="mt-4 text-center">
            <button
              onClick={() => setShowPermissions(!showPermissions)}
              className="inline-flex items-center gap-1 text-sm text-catch-black/50 hover:text-catch-black transition-colors"
            >
              {showPermissions ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              What permissions are needed?
            </button>

            {showPermissions && (
              <div className="mt-3 flex items-center gap-4 bg-catch-beige rounded-xl p-3">
                <div className="text-left text-xs text-catch-black/60 space-y-1.5 flex-1">
                  <div className="flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-catch-black/40 flex-shrink-0" />
                    <span>Access your basic profile information</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-catch-black/40 flex-shrink-0" />
                    <span>You'll be asked for additional permissions during setup</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="mt-8 text-sm text-catch-black/40 text-center">
            By signing in, you agree to our{' '}
            <a href="/terms-new" className="underline hover:text-catch-black/60 transition-colors">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="underline hover:text-catch-black/60 transition-colors">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  )
}
